'use client'

import {
    getPublicCheckout,
    payPublicCheckout,
    PublicCheckoutData,
} from '@/lib/api/financial/financial-checkout-public-api'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
    MdCheckCircle,
    MdContentCopy,
    MdCreditCard,
    MdError,
    MdHourglassTop,
    MdPayment,
    MdQrCode,
} from 'react-icons/md'
import { useParams, useSearchParams } from 'react-router-dom'
import Drawer from '@/components/drawer/drawer'

function formatCurrency(cents: number): string {
    return (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })
}

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('pt-BR')
}

export default function BillingCheckoutPage() {
    const { token } = useParams<{ token: string }>()
    const [searchParams] = useSearchParams()
    const returnUrl = searchParams.get('return_url')
    // Origem do checkout ('flex' — portal de procedimento; 'app' — dentro do
    // sistema, pagando mensalidade/pacote). Ajusta o rótulo do retorno; o destino
    // em si vem de `return_url`.
    const origin = searchParams.get('from')
    const fromFlex = origin === 'flex'
    const fromApp = origin === 'app'

    const [loading, setLoading] = useState(true)
    const [checkout, setCheckout] = useState<PublicCheckoutData | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [processing, setProcessing] = useState(false)
    const [paymentSuccess, setPaymentSuccess] = useState(false)
    const [awaitingConfirmation, setAwaitingConfirmation] = useState(false)
    const [paymentError, setPaymentError] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Card form
    const [cardHolderName, setCardHolderName] = useState('')
    const [cardNumber, setCardNumber] = useState('')
    const [expiryMonth, setExpiryMonth] = useState('')
    const [expiryYear, setExpiryYear] = useState('')
    const [ccv, setCcv] = useState('')
    const [holderName, setHolderName] = useState('')
    const [holderEmail, setHolderEmail] = useState('')
    const [holderCpfCnpj, setHolderCpfCnpj] = useState('')
    const [holderPostalCode, setHolderPostalCode] = useState('')
    const [holderAddressNumber, setHolderAddressNumber] = useState('')
    const [holderPhone, setHolderPhone] = useState('')
    const [installmentCount, setInstallmentCount] = useState(1)
    const [acceptedPolicy, setAcceptedPolicy] = useState(false)
    const [policyDrawerOpen, setPolicyDrawerOpen] = useState(false)

    // Escolha de método quando a cobrança ainda não tem método definido.
    const [selectedMethod, setSelectedMethod] = useState<'CREDIT_CARD' | null>(
        null
    )
    const [generatingPix, setGeneratingPix] = useState(false)

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
        }
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current)
            pollingTimeoutRef.current = null
        }
    }, [])

    const startPolling = useCallback(
        (timeoutMs = 300000, intervalMs = 3000) => {
            if (!token) return
            stopPolling()

            const applyResult = (result: PublicCheckoutData) => {
                stopPolling()
                setAwaitingConfirmation(false)
                setPaymentSuccess(true)
                setCheckout(result)
                // Redirecionar automaticamente se veio de uma loja
                if (returnUrl) {
                    setTimeout(() => {
                        window.location.href = returnUrl
                    }, 3000)
                }
            }

            pollingRef.current = setInterval(async () => {
                try {
                    const response = await getPublicCheckout(token)
                    if (
                        response.success &&
                        response.result?.status === 'PAID'
                    ) {
                        applyResult(response.result)
                    }
                } catch {
                    // ignora erros de polling
                }
            }, intervalMs)

            pollingTimeoutRef.current = setTimeout(async () => {
                stopPolling()
                // Antes de desistir, faz uma ultima verificacao — evita travar em
                // "Processando" caso a confirmacao tenha chegado entre os polls.
                try {
                    const response = await getPublicCheckout(token)
                    if (
                        response.success &&
                        response.result?.status === 'PAID'
                    ) {
                        applyResult(response.result)
                        return
                    }
                } catch {
                    // ignora — cai no aviso abaixo
                }
                setAwaitingConfirmation(false)
                setPaymentError(
                    'Ainda não recebemos a confirmação do pagamento. Se você já pagou, aguarde alguns instantes e atualize a página.'
                )
            }, timeoutMs)
        },
        [token, stopPolling, returnUrl]
    )

    useEffect(() => {
        if (token) {
            loadCheckout()
        }
    }, [token])

    useEffect(() => {
        return () => stopPolling()
    }, [stopPolling])

    /**
     * Parcelamento travado: o estado acompanha o combinado, senão o total exibido
     * seria o da à vista enquanto a cobrança sai parcelada.
     *
     * ⚠️ Fica AQUI, junto dos outros hooks, e não perto do JSX que o usa: abaixo há
     * returns antecipados (carregando, não encontrada, paga…). Um hook depois deles é
     * chamado só em parte dos renders, e o React aborta a tela inteira com "Rendered
     * more hooks than during the previous render".
     */
    useEffect(() => {
        const travado = checkout?.installmentsLocked
            ? checkout?.installmentOptions?.[0]
            : null
        if (travado && travado > 0) setInstallmentCount(travado)
    }, [checkout?.installmentsLocked, checkout?.installmentOptions])

    const loadCheckout = async () => {
        if (!token) return
        setLoading(true)
        try {
            const response = await getPublicCheckout(token)
            if (response.success && response.result) {
                setCheckout(response.result)
                // Iniciar polling para PIX pendente (aguardando confirmação de pagamento)
                // Timeout de 30 minutos com intervalo de 5 segundos para PIX manual
                if (
                    response.result.paymentMethod === 'PIX' &&
                    response.result.status === 'PENDING'
                ) {
                    setAwaitingConfirmation(true)
                    startPolling(30 * 60 * 1000, 5000)
                }
                // Se já está pago, mostrar sucesso
                if (response.result.status === 'PAID') {
                    setPaymentSuccess(true)
                }
            } else {
                setError(response.error || 'Cobrança não encontrada')
            }
        } catch {
            setError('Erro ao carregar dados da cobrança')
        } finally {
            setLoading(false)
        }
    }

    const handleCopy = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            // fallback silencioso
        }
    }

    const formatCardNumber = (value: string) => {
        const digits = value.replace(/\D/g, '').slice(0, 16)
        return digits.replace(/(\d{4})(?=\d)/g, '$1 ')
    }

    const formatCpfCnpj = (value: string) => {
        const digits = value.replace(/\D/g, '')
        if (digits.length <= 11) {
            return digits
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
        }
        return digits
            .replace(/(\d{2})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1/$2')
            .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
    }

    const formatPostalCode = (value: string) => {
        const digits = value.replace(/\D/g, '').slice(0, 8)
        return digits.replace(/(\d{5})(\d)/, '$1-$2')
    }

    const formatPhone = (value: string) => {
        const digits = value.replace(/\D/g, '').slice(0, 11)
        if (digits.length <= 2) return digits
        if (digits.length <= 7)
            return `(${digits.slice(0, 2)}) ${digits.slice(2)}`
        if (digits.length <= 10)
            return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
    }

    // Gera a cobrança PIX on-demand quando o pagador escolhe PIX. Após gerar,
    // recarrega o checkout (que passa a ter os dados de PIX + inicia o polling).
    const handlePixGenerate = async () => {
        if (!token) return
        // O backend exige o aceite da política para QUALQUER método (inclusive
        // PIX). Antes o checkbox só aparecia no cartão, então o PIX era barrado
        // no backend sem o pagador ter como aceitar. Agora o aceite é exigido e
        // enviado aqui também.
        if (checkout?.paymentPolicy && !acceptedPolicy) {
            setPaymentError(
                'É necessário aceitar a Política de Pagamento para continuar.'
            )
            return
        }
        setGeneratingPix(true)
        setPaymentError(null)
        try {
            const response = await payPublicCheckout(token, {
                billingType: 'PIX',
                acceptedPaymentPolicy: checkout?.paymentPolicy
                    ? acceptedPolicy
                    : undefined,
            })
            if (response.success) {
                await loadCheckout()
            } else {
                setPaymentError(response.error || 'Erro ao gerar cobrança PIX')
            }
        } catch {
            setPaymentError('Erro ao gerar cobrança PIX. Tente novamente.')
        } finally {
            setGeneratingPix(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!token || !checkout) return

        if (checkout.paymentPolicy && !acceptedPolicy) {
            setPaymentError(
                'É necessário aceitar a Política de Pagamento para continuar.'
            )
            return
        }

        setProcessing(true)
        setPaymentError(null)

        try {
            const response = await payPublicCheckout(token, {
                billingType: 'CREDIT_CARD',
                creditCard: {
                    holderName: cardHolderName,
                    number: cardNumber.replace(/\s/g, ''),
                    expiryMonth,
                    expiryYear,
                    ccv,
                },
                creditCardHolderInfo: {
                    name: holderName || cardHolderName,
                    email: holderEmail,
                    cpfCnpj: holderCpfCnpj.replace(/\D/g, ''),
                    postalCode: holderPostalCode.replace(/\D/g, ''),
                    addressNumber: holderAddressNumber,
                    phone: holderPhone.replace(/\D/g, '') || undefined,
                },
                installmentCount:
                    installmentCount > 1 ? installmentCount : undefined,
                acceptedPaymentPolicy: checkout.paymentPolicy
                    ? acceptedPolicy
                    : undefined,
            })

            if (response.success) {
                setAwaitingConfirmation(true)
                startPolling()
            } else {
                setPaymentError(response.error || 'Erro ao processar pagamento')
            }
        } catch {
            setPaymentError('Erro ao processar pagamento. Tente novamente.')
        } finally {
            setProcessing(false)
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="loading loading-spinner loading-lg text-blue-600"></div>
                    <p className="text-gray-500">Carregando...</p>
                </div>
            </div>
        )
    }

    if (error || !checkout) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="card bg-white shadow-xl !max-w-md w-full">
                    <div className="card-body text-center">
                        <MdError className="text-6xl text-red-500 mx-auto mb-4" />
                        <h2 className="card-title justify-center text-xl text-gray-800">
                            Cobrança não encontrada
                        </h2>
                        <p className="text-gray-500">
                            {error ||
                                'O link de pagamento é inválido ou expirou.'}
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    if (awaitingConfirmation && checkout.paymentMethod !== 'PIX') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="card bg-white shadow-xl !max-w-md w-full">
                    <div className="card-body text-center">
                        <MdHourglassTop className="text-6xl text-blue-500 mx-auto mb-4 animate-pulse" />
                        <h2 className="card-title justify-center text-xl text-gray-800">
                            Processando Pagamento
                        </h2>
                        <p className="text-gray-500 mb-4">
                            Seu pagamento de{' '}
                            <strong>{formatCurrency(checkout.amount)}</strong>{' '}
                            está sendo processado. Aguarde a confirmação.
                        </p>
                        <div className="flex justify-center mb-4">
                            <span className="loading loading-dots loading-lg text-blue-600"></span>
                        </div>
                        <p className="text-xs text-gray-400">
                            Isso pode levar alguns segundos. Não feche esta
                            página.
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    // Destino e rótulo da volta, na ordem em que cada um é confiável: o link
    // pediu (`return_url`), a cobrança sabe (loja), ou o histórico do navegador.
    const backHref =
        returnUrl || checkout.storeOrdersUrl || checkout.storeUrl || null
    const canGoBack = typeof window !== 'undefined' && window.history.length > 1
    const backLabel = fromFlex
        ? 'Voltar para o acompanhamento'
        : fromApp
          ? 'Voltar para o sistema'
          : checkout.storeOrdersUrl && checkout.storeName
            ? 'Ver meus pedidos'
            : checkout.storeUrl
              ? 'Voltar para a loja'
              : 'Voltar'

    if (checkout.status === 'PAID' || paymentSuccess) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="card bg-white shadow-xl !max-w-md w-full">
                    <div className="card-body text-center">
                        <MdCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
                        <h2 className="card-title justify-center text-xl text-gray-800">
                            Pagamento Confirmado
                        </h2>
                        <p className="text-gray-500 mb-4">
                            Seu pagamento de{' '}
                            <strong>{formatCurrency(checkout.amount)}</strong>{' '}
                            foi processado com sucesso.
                        </p>
                        <div className="bg-gray-100 rounded-lg p-4">
                            <p className="text-sm text-gray-500">
                                {checkout.storeName ? 'Loja' : 'Empresa'}
                            </p>
                            <p className="font-semibold text-gray-800">
                                {checkout.storeName || checkout.companyName}
                            </p>
                            {checkout.description && (
                                <>
                                    <p className="text-sm text-gray-500 mt-2">
                                        Descrição
                                    </p>
                                    <p className="text-gray-800">
                                        {checkout.description}
                                    </p>
                                </>
                            )}
                        </div>
                        {/* A volta do pagador. `return_url` vem no link e redireciona
                            sozinho; o endereço da loja é o reforço para quem abriu o link
                            pelo WhatsApp ou e-mail, onde o parâmetro não está; e o
                            histórico é o último recurso — sem nenhum dos três, o
                            pagamento terminava numa tela sem saída. */}
                        {backHref ? (
                            <a
                                href={backHref}
                                className="btn btn-secondary w-full mt-4"
                            >
                                {backLabel}
                            </a>
                        ) : (
                            canGoBack && (
                                <button
                                    type="button"
                                    className="btn btn-secondary w-full mt-4"
                                    onClick={() => window.history.back()}
                                >
                                    {backLabel}
                                </button>
                            )
                        )}
                        {checkout.storeUrl && (
                            <a
                                href={checkout.storeUrl}
                                className="btn btn-ghost btn-sm w-full mt-2"
                            >
                                Continuar comprando
                            </a>
                        )}
                        {returnUrl && (
                            <p className="text-xs text-gray-400 mt-2">
                                Você será redirecionado automaticamente em
                                alguns segundos...
                            </p>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    if (checkout.status === 'CANCELLED') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="card bg-white shadow-xl !max-w-md w-full">
                    <div className="card-body text-center">
                        <MdError className="text-6xl text-amber-500 mx-auto mb-4" />
                        <h2 className="card-title justify-center text-xl text-gray-800">
                            Cobrança Cancelada
                        </h2>
                        <p className="text-gray-500">
                            Esta cobrança foi cancelada e não pode mais ser
                            paga.
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    const installmentOptions: number[] =
        checkout.installmentOptions && checkout.installmentOptions.length > 0
            ? checkout.installmentOptions
            : [1]

    /**
     * Valor de cada parcela no crédito, com a taxa da faixa.
     *
     * Dividir `checkout.amount` pelas parcelas mostraria menos do que será cobrado: o
     * valor gravado carrega a taxa da à vista, e ela é reajustada para a escolha do
     * pagador ao gerar a cobrança. Sem `paymentOptions` (empresa sem taxa configurada),
     * a divisão simples continua correta.
     */
    const creditOptionFor = (count: number) =>
        checkout.paymentOptions?.find(
            o => o.method === 'CREDIT_CARD' && o.installments === count
        )

    const installmentLabelFor = (count: number): string => {
        const option = creditOptionFor(count)
        const perInstallment = option
            ? option.installmentAmount
            : Math.ceil(checkout.amount / count)
        return `${count}x de ${formatCurrency(perInstallment)}`
    }

    // Aceite da Política de Pagamento — reusado em TODOS os pontos de pagamento
    // (PIX, cartão, escolha de método). Antes vivia só no form do cartão, então
    // no PIX ficava oculto e o backend barrava o pagamento sem o pagador poder
    // aceitar. O estado `acceptedPolicy` é compartilhado entre os pontos.
    const renderPolicyAcceptance = () =>
        checkout.paymentPolicy ? (
            <label className="flex items-start gap-3 mb-4 cursor-pointer">
                <input
                    type="checkbox"
                    className="checkbox checkbox-secondary mt-0.5"
                    checked={acceptedPolicy}
                    onChange={e => setAcceptedPolicy(e.target.checked)}
                />
                <span className="text-sm text-gray-600">
                    Li e aceito a{' '}
                    <button
                        type="button"
                        onClick={() => setPolicyDrawerOpen(true)}
                        className="text-blue-600 hover:underline font-semibold"
                    >
                        Política de Pagamento
                    </button>
                    .
                </span>
            </label>
        ) : null

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="card bg-white shadow-xl !max-w-lg w-full relative">
                <img
                    src="https://baas.asaas.com/selos/Servicos_financeiros_Asaas-Reduzida-Positivo.svg?id=d6866c01-f11b-417d-b544-09d572b65e19"
                    alt="Asaas"
                    width={90}
                    className="object-contain absolute top-5 right-5"
                />

                <div className="card-body">
                    {/* Header */}
                    <div className="text-center mb-6">
                        {(checkout.storeLogo || checkout.companyLogo) && (
                            <img
                                src={
                                    checkout.storeLogo || checkout.companyLogo!
                                }
                                alt={checkout.storeName || checkout.companyName}
                                className="h-12 mx-auto mb-3 object-contain"
                            />
                        )}
                        <h1 className="text-xl font-bold text-gray-800">
                            {checkout.storeName || checkout.companyName}
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">
                            Pagamento de cobrança
                        </p>
                    </div>

                    {/* Resumo */}
                    <div className="bg-gray-100 rounded-lg p-4 mb-6">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-500">Valor</span>
                            <span className="text-2xl font-bold text-blue-600">
                                {formatCurrency(checkout.amount)}
                            </span>
                        </div>
                        {checkout.description && (
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-gray-500">Descrição</span>
                                <span className="text-sm text-gray-800">
                                    {checkout.description}
                                </span>
                            </div>
                        )}
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-500">Vencimento</span>
                            <span className="text-sm font-medium text-gray-800">
                                {formatDate(checkout.dueDate)}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-500">Cliente</span>
                            <span className="text-sm font-medium text-gray-800">
                                {checkout.clientName}
                            </span>
                        </div>
                    </div>

                    {/* PIX */}
                    {checkout.paymentMethod === 'PIX' && (
                        <div>
                            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-800">
                                <MdQrCode className="text-xl" />
                                Pagamento via PIX
                            </h2>

                            {/* Rede de segurança: normalmente o PIX já é gerado na
                                criação da cobrança e o QR aparece direto. Se a
                                geração falhou (gateway fora na hora), o pagador
                                gera aqui, on-demand. */}
                            {!checkout.pixQrCodeBase64 &&
                                !checkout.pixCopyPaste &&
                                !checkout.pixKey && (
                                    <div className="mb-4">
                                        {paymentError && (
                                            <div className="alert alert-error mb-3">
                                                <MdError className="text-lg" />
                                                <span className="text-sm">
                                                    {paymentError}
                                                </span>
                                            </div>
                                        )}
                                        {renderPolicyAcceptance()}
                                        <button
                                            type="button"
                                            onClick={handlePixGenerate}
                                            disabled={
                                                generatingPix ||
                                                (!!checkout.paymentPolicy &&
                                                    !acceptedPolicy)
                                            }
                                            className="btn btn-secondary w-full gap-2"
                                        >
                                            {generatingPix ? (
                                                <>
                                                    <span className="loading loading-spinner loading-sm"></span>
                                                    Gerando PIX...
                                                </>
                                            ) : (
                                                <>
                                                    <MdQrCode className="text-lg" />
                                                    Gerar PIX para pagar
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}

                            {checkout.pixQrCodeBase64 && (
                                <div className="flex justify-center mb-4">
                                    <img
                                        src={`data:image/png;base64,${checkout.pixQrCodeBase64}`}
                                        alt="QR Code PIX"
                                        className="w-56 h-56 rounded-lg border border-gray-200"
                                    />
                                </div>
                            )}

                            {checkout.pixKey && (
                                <div className="mb-4">
                                    <label className="text-sm text-gray-500 mb-1 block">
                                        Chave PIX
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            readOnly
                                            value={checkout.pixKey}
                                            className="input input-bordered w-full font-mono text-sm bg-white border-gray-300"
                                        />
                                        <button
                                            onClick={() =>
                                                handleCopy(checkout.pixKey!)
                                            }
                                            className="btn btn-secondary btn-square"
                                            title="Copiar"
                                        >
                                            {copied ? (
                                                <MdCheckCircle className="text-lg" />
                                            ) : (
                                                <MdContentCopy className="text-lg" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {checkout.pixCopyPaste && (
                                <div className="mb-4">
                                    <label className="text-sm text-gray-500 mb-1 block">
                                        Código copia e cola
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            readOnly
                                            value={checkout.pixCopyPaste}
                                            className="input input-bordered w-full font-mono text-xs bg-white border-gray-300"
                                        />
                                        <button
                                            onClick={() =>
                                                handleCopy(
                                                    checkout.pixCopyPaste!
                                                )
                                            }
                                            className="btn btn-secondary btn-square"
                                            title="Copiar"
                                        >
                                            {copied ? (
                                                <MdCheckCircle className="text-lg" />
                                            ) : (
                                                <MdContentCopy className="text-lg" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {(checkout.pixQrCodeBase64 ||
                                checkout.pixCopyPaste ||
                                checkout.pixKey) && (
                                <div className="alert alert-info">
                                    <span className="text-sm">
                                        {checkout.pixQrCodeBase64 ||
                                        checkout.pixCopyPaste
                                            ? 'Escaneie o QR Code ou copie o código acima e cole no aplicativo do seu banco para realizar o pagamento.'
                                            : 'Copie a chave PIX acima e realize a transferência pelo aplicativo do seu banco.'}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* BOLETO */}
                    {checkout.paymentMethod === 'BOLETO' && (
                        <div>
                            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-800">
                                <MdPayment className="text-xl" />
                                Pagamento via Boleto
                            </h2>

                            {checkout.digitableLine && (
                                <div className="mb-4">
                                    <label className="text-sm text-gray-500 mb-1 block">
                                        Linha digitável
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            readOnly
                                            value={checkout.digitableLine}
                                            className="input input-bordered w-full font-mono text-xs bg-white border-gray-300"
                                        />
                                        <button
                                            onClick={() =>
                                                handleCopy(
                                                    checkout.digitableLine!
                                                )
                                            }
                                            className="btn btn-secondary btn-square"
                                            title="Copiar"
                                        >
                                            {copied ? (
                                                <MdCheckCircle className="text-lg" />
                                            ) : (
                                                <MdContentCopy className="text-lg" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {checkout.bankSlipUrl && (
                                <a
                                    href={checkout.bankSlipUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-secondary w-full"
                                >
                                    Visualizar Boleto
                                </a>
                            )}
                        </div>
                    )}

                    {/* CREDIT CARD */}
                    {(checkout.paymentMethod === 'CREDIT_CARD' ||
                        selectedMethod === 'CREDIT_CARD') && (
                        <form onSubmit={handleSubmit}>
                            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-800">
                                <MdCreditCard className="text-xl" />
                                Dados do Cartão
                            </h2>

                            {!checkout.paymentMethod && (
                                <button
                                    type="button"
                                    onClick={() => setSelectedMethod(null)}
                                    className="text-sm text-blue-600 hover:underline mb-4"
                                >
                                    ← Escolher outra forma de pagamento
                                </button>
                            )}

                            {paymentError && (
                                <div className="alert alert-error mb-4">
                                    <MdError className="text-lg" />
                                    <span className="text-sm">
                                        {paymentError}
                                    </span>
                                </div>
                            )}

                            <div className="space-y-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text text-gray-700">
                                            Nome no cartão
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full bg-white border-gray-300"
                                        value={cardHolderName}
                                        onChange={e =>
                                            setCardHolderName(e.target.value)
                                        }
                                        placeholder="Nome como está no cartão"
                                        required
                                    />
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text text-gray-700">
                                            Número do cartão
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full bg-white border-gray-300"
                                        value={cardNumber}
                                        onChange={e =>
                                            setCardNumber(
                                                formatCardNumber(e.target.value)
                                            )
                                        }
                                        placeholder="0000 0000 0000 0000"
                                        maxLength={19}
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-3 gap-3">
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text text-gray-700">
                                                Mês
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full bg-white border-gray-300"
                                            value={expiryMonth}
                                            onChange={e =>
                                                setExpiryMonth(
                                                    e.target.value
                                                        .replace(/\D/g, '')
                                                        .slice(0, 2)
                                                )
                                            }
                                            placeholder="MM"
                                            maxLength={2}
                                            required
                                        />
                                    </div>
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text text-gray-700">
                                                Ano
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full bg-white border-gray-300"
                                            value={expiryYear}
                                            onChange={e =>
                                                setExpiryYear(
                                                    e.target.value
                                                        .replace(/\D/g, '')
                                                        .slice(0, 4)
                                                )
                                            }
                                            placeholder="AAAA"
                                            maxLength={4}
                                            required
                                        />
                                    </div>
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text text-gray-700">
                                                CVV
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full bg-white border-gray-300"
                                            value={ccv}
                                            onChange={e =>
                                                setCcv(
                                                    e.target.value
                                                        .replace(/\D/g, '')
                                                        .slice(0, 4)
                                                )
                                            }
                                            placeholder="000"
                                            maxLength={4}
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Parcelas combinadas na venda: mostra o número, sem
                                    seletor. O operador já fechou "3x" com o paciente e
                                    a taxa foi calculada nessa faixa — trocar aqui
                                    mudaria o custo por baixo do valor acordado (o
                                    backend também recusa). */}
                                {checkout.installmentsLocked &&
                                    installmentOptions.length > 0 && (
                                        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                                            <p className="text-xs text-gray-500">
                                                Parcelamento
                                            </p>
                                            <p className="text-sm font-semibold text-gray-800">
                                                {installmentLabelFor(
                                                    installmentOptions[0]
                                                )}
                                            </p>
                                        </div>
                                    )}

                                {/* Parcelas */}
                                {!checkout.installmentsLocked &&
                                    installmentOptions.length > 1 && (
                                        <div className="form-control">
                                            <label className="label">
                                                <span className="label-text text-gray-700">
                                                    Parcelas
                                                </span>
                                            </label>
                                            <select
                                                className="select select-bordered w-full bg-white border-gray-300"
                                                value={installmentCount}
                                                onChange={e =>
                                                    setInstallmentCount(
                                                        parseInt(e.target.value)
                                                    )
                                                }
                                            >
                                                {installmentOptions.map(
                                                    count => (
                                                        <option
                                                            key={count}
                                                            value={count}
                                                        >
                                                            {installmentLabelFor(
                                                                count
                                                            )}
                                                            {count === 1
                                                                ? ' (à vista)'
                                                                : ''}
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </div>
                                    )}

                                <div className="divider text-xs text-gray-400">
                                    Dados do titular
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text text-gray-700">
                                            CPF/CNPJ do titular
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full bg-white border-gray-300"
                                        value={holderCpfCnpj}
                                        onChange={e =>
                                            setHolderCpfCnpj(
                                                formatCpfCnpj(e.target.value)
                                            )
                                        }
                                        placeholder="000.000.000-00"
                                        maxLength={18}
                                        required
                                    />
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text text-gray-700">
                                            Email do titular
                                        </span>
                                    </label>
                                    <input
                                        type="email"
                                        className="input input-bordered w-full bg-white border-gray-300"
                                        value={holderEmail}
                                        onChange={e =>
                                            setHolderEmail(e.target.value)
                                        }
                                        placeholder="email@exemplo.com"
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text text-gray-700">
                                                CEP
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full bg-white border-gray-300"
                                            value={holderPostalCode}
                                            onChange={e =>
                                                setHolderPostalCode(
                                                    formatPostalCode(
                                                        e.target.value
                                                    )
                                                )
                                            }
                                            placeholder="00000-000"
                                            maxLength={9}
                                            required
                                        />
                                    </div>
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text text-gray-700">
                                                Número
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full bg-white border-gray-300"
                                            value={holderAddressNumber}
                                            onChange={e =>
                                                setHolderAddressNumber(
                                                    e.target.value
                                                )
                                            }
                                            placeholder="123"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text text-gray-700">
                                            Telefone
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full bg-white border-gray-300"
                                        value={holderPhone}
                                        onChange={e =>
                                            setHolderPhone(
                                                formatPhone(e.target.value)
                                            )
                                        }
                                        placeholder="(00) 00000-0000"
                                        maxLength={15}
                                    />
                                </div>
                            </div>

                            {/* Aceite da Política de Pagamento */}
                            <div className="mt-6">
                                {renderPolicyAcceptance()}
                            </div>

                            <button
                                type="submit"
                                className="btn btn-secondary w-full mt-2"
                                disabled={
                                    processing ||
                                    (!!checkout.paymentPolicy &&
                                        !acceptedPolicy)
                                }
                            >
                                {processing ? (
                                    <>
                                        <span className="loading loading-spinner loading-sm"></span>
                                        Processando...
                                    </>
                                ) : (
                                    <>
                                        <MdCreditCard className="text-lg" />
                                        {/* O total do cartão depende da faixa de
                                            parcelas escolhida acima; prometer o valor
                                            da à vista no botão e cobrar outro é a
                                            mesma divergência que este ajuste corrige. */}
                                        Pagar{' '}
                                        {formatCurrency(
                                            creditOptionFor(installmentCount)
                                                ?.total ?? checkout.amount
                                        )}
                                    </>
                                )}
                            </button>
                        </form>
                    )}

                    {/* Sem método definido: pagador escolhe como pagar */}
                    {!checkout.paymentMethod && !selectedMethod && (
                        <div>
                            {paymentError && (
                                <div className="alert alert-error mb-4">
                                    <MdError className="text-lg" />
                                    <span className="text-sm">
                                        {paymentError}
                                    </span>
                                </div>
                            )}
                            {checkout.availableMethods &&
                            (checkout.availableMethods.pix ||
                                checkout.availableMethods.creditCard) ? (
                                <>
                                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-800">
                                        <MdPayment className="text-xl" />
                                        Como você quer pagar?
                                    </h2>
                                    {renderPolicyAcceptance()}
                                    <div className="flex flex-col gap-3">
                                        {checkout.availableMethods.pix && (
                                            <button
                                                type="button"
                                                onClick={handlePixGenerate}
                                                disabled={
                                                    generatingPix ||
                                                    (!!checkout.paymentPolicy &&
                                                        !acceptedPolicy)
                                                }
                                                className="btn btn-outline btn-secondary justify-start gap-3 h-14"
                                            >
                                                {generatingPix ? (
                                                    <span className="loading loading-spinner loading-sm"></span>
                                                ) : (
                                                    <MdQrCode className="text-2xl" />
                                                )}
                                                <span className="text-left">
                                                    <span className="block font-semibold">
                                                        PIX
                                                    </span>
                                                    <span className="block text-xs opacity-70">
                                                        Aprovação imediata
                                                    </span>
                                                </span>
                                            </button>
                                        )}
                                        {checkout.availableMethods
                                            .creditCard && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setSelectedMethod(
                                                        'CREDIT_CARD'
                                                    )
                                                }
                                                className="btn btn-outline btn-secondary justify-start gap-3 h-14"
                                            >
                                                <MdCreditCard className="text-2xl" />
                                                <span className="text-left">
                                                    <span className="block font-semibold">
                                                        Cartão de Crédito
                                                    </span>
                                                    <span className="block text-xs opacity-70">
                                                        Parcelamento disponível
                                                    </span>
                                                </span>
                                            </button>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="text-center">
                                    <p className="text-gray-500">
                                        Esta cobrança ainda não possui um método
                                        de pagamento disponível. Entre em
                                        contato com{' '}
                                        <strong>
                                            {checkout.storeName ||
                                                checkout.companyName}
                                        </strong>{' '}
                                        para mais informações.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {checkout.paymentPolicy && (
                <Drawer
                    isOpen={policyDrawerOpen}
                    onClose={() => setPolicyDrawerOpen(false)}
                    title="Política de Pagamento"
                    width="w-full sm:w-[500px] md:w-[600px] lg:w-[700px]"
                >
                    <div className="p-6">
                        <div
                            className="prose prose-sm sm:prose-base max-w-none"
                            style={{ lineHeight: '1.6' }}
                            dangerouslySetInnerHTML={{
                                __html: checkout.paymentPolicy.content,
                            }}
                        />
                    </div>
                </Drawer>
            )}
        </div>
    )
}
